import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export interface BaseHttpResponse<T> {
  Result: boolean;
  StatusCode: number;
  ErrorCode: any;
  ErrorText: any;
  Data: T;
  DataCount: number;
  IsErrorMessageUserFriendly: boolean;
  ResultAction: any;
  ErrorTextWithCode: string;
}

export interface BaseEntityParams {
  Id?: number;
  Take?: number;
  Skip?: number;
  Search?: string;
  Entity?: any;
  WithCount?: boolean;

  [key: string]: any;
}

export interface PagingResponse<T> {
  items: T[];
  total: number;
}

const clientAPIInstance: AxiosInstance = axios.create({
  baseURL: process.env.ORIGIN_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  }
});

const remoteAPIInstance: AxiosInstance = axios.create({
  baseURL: process.env.API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  }
});

const setupInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    config => config,
    error => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    error => {
      if (error.response?.status === 401) {
        // Handle unauthorized errors
      }
      return Promise.reject(error);
    }
  );
};

setupInterceptors(clientAPIInstance);
setupInterceptors(remoteAPIInstance);

const request = async <T>(
  instance: AxiosInstance,
  method: AxiosRequestConfig["method"],
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response = await instance.request<T>({
      method,
      url,
      data,
      ...config
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

const createApiMethods = (instance: AxiosInstance) => ({
  _instance: instance,
  get: async <T>(url: string, config?: AxiosRequestConfig) => {
    return request<T>(instance, "get", url, undefined, config);
  },
  post: async <T, U>(url: string, data: U, config?: AxiosRequestConfig) => {
    return request<T>(instance, "post", url, data, config);
  },
  put: async <T, U>(url: string, data: U, config?: AxiosRequestConfig) => {
    return request<T>(instance, "put", url, data, config);
  },
  delete: async <T>(url: string, config?: AxiosRequestConfig) => {
    return request<T>(instance, "delete", url, undefined, config);
  },
  _setHeaderToken: (token: string) => {
    instance.defaults.headers.token = token;
  },
  _setAuthorization: (token: string) => {
    instance.defaults.headers.Authorization = `Bearer ${token}`;
  }
});

export const clearCache = async () => {
  // await (remoteAPIInstance as AxiosCacheInstance).storage.clear();
  // await (clientAPIInstance as AxiosCacheInstance).storage.clear();
};

const Client = createApiMethods(clientAPIInstance);
const Remote = createApiMethods(remoteAPIInstance);

const API = {
  Client,
  Remote
};

export default API;
